import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { GlobalTable, HeaderLine, Td, Tr } from '../../../../../components/styles/table-style';
import { Eye, FileText, Paperclip, Settings, ArrowUpDown, ChevronUp, ChevronDown, Send, Ban } from 'lucide-react';
import { Spinner } from '../cardStyles';
import ModalContext from '../../../../../contexts/ModalContext';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import { STATUS_COLORS, mapStatus } from '../../invoiceStatus';
import { useEmissionPolling } from '../useEmissionPolling';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Th,
  ThContent,
  ConfigMenuWrapper,
  ConfigMenuButton,
  ColumnMenu,
  ColumnMenuList,
  ColumnMenuItem,
  HiddenCheckbox,
  StyledCheckbox,
} from './style';

const Badge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${props => STATUS_COLORS[props.type]?.bg || '#f2f2f2'};
  color: ${props => STATUS_COLORS[props.type]?.text || '#666'};
  white-space: nowrap;
`;

const Dot = styled.span<{ type: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${props => STATUS_COLORS[props.type]?.dot || '#999'};
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  width: 100%;
`;

const InvoiceTable: React.FC = () => {
  const { setModalActive } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<{ id: number; label: string } | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const columnMenuRef = useRef<HTMLTableCellElement>(null);

  const { refresh, search, appliedFilters } = useOutletContext<{ refresh?: boolean; search?: string; appliedFilters?: any[] }>() ?? {};

  const loadInvoices = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params: any = { take: 50, skip: 0 };
      if (search && search.trim() !== '') {
        params.search = search;
      }
      if (appliedFilters && appliedFilters.length > 0) {
        params.filters = JSON.stringify(appliedFilters);
      }
      const response = await ApiService.fiscal.getInvoices(params);
      const data = response.data;
      setInvoices(Array.isArray(data) ? data : (data?.data || []));
      if (!silent) setError(null);
    } catch {
      if (!silent) setError('Erro ao carregar as notas fiscais.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, appliedFilters]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices, refresh]);

  const hasPending = useMemo(
    () => invoices.some((i: any) => i.status === 'EMITINDO'),
    [invoices],
  );

  // Sincroniza cada nota EMITINDO com o Nota Gateway (nfe-status grava EMITIDA/NEGADA)
  // e só então recarrega a lista. Reler sozinho deixaria as notas presas em EMITINDO.
  const syncPending = useCallback(async () => {
    const pendentes = invoices.filter((i: any) => i.status === 'EMITINDO');
    await Promise.allSettled(
      pendentes.map((i: any) => ApiService.fiscal.nfeStatus(i.id)),
    );
    await loadInvoices(true);
  }, [invoices, loadInvoices]);
  useEmissionPolling({ isEmitindo: hasPending, onTick: syncPending });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openModal = useCallback((id?: number) => {
    setModalActive(false);
    navigate(id ? `/panel/invoice/edit/${id}` : '/panel/invoice/create');
  }, [navigate, setModalActive]);

  const openView = useCallback((id: number) => {
    navigate(`/panel/invoice/view/${id}`);
  }, [navigate]);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await ApiService.fiscal.cancelar(cancelTarget.id);
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      setCancelTarget(null);
      loadInvoices(true);
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    } finally {
      setCancelLoading(false);
    }
  }, [cancelTarget, setFeedback, loadInvoices]);

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'numeroNF',
      header: 'NF',
      accessorFn: (row) =>
        row.serie && row.numeroNF
          ? `${row.serie}.${String(row.numeroNF).padStart(6, '0')}`
          : '—',
      cell: ({ getValue }) => <strong>{getValue() as string}</strong>,
    },
    {
      accessorKey: 'orderId',
      header: 'Pedido',
      cell: ({ getValue }) => <span style={{ color: '#555', fontSize: '13px' }}>{(getValue() as any) || '—'}</span>,
    },
    {
      accessorKey: 'chaveAcesso',
      header: 'Chave de acesso',
      cell: ({ getValue }) => <span style={{ color: '#999', fontSize: '12px' }}>{(getValue() as string) || '—'}</span>,
    },
    {
      accessorKey: 'destinatario',
      header: 'Destinatário',
      accessorFn: (row) => row.CompanyCliente?.fantasyName || row.CompanyCliente?.companyName || '—',
    },
    {
      accessorKey: 'cfop',
      header: 'CFOP',
      accessorFn: (row) =>
        row.InvoiceProduct?.[0]?.cfop || row.InvoiceService?.[0]?.cfop || '—',
      cell: ({ getValue }) => {
        const v = getValue() as string;
        return v && v !== '—' ? <strong style={{ color: '#0085CC' }}>{v}</strong> : <span>—</span>;
      },
    },
    {
      accessorKey: 'valorTotal',
      header: 'Valor total',
      accessorFn: (row) => {
        const prods = row.InvoiceProduct?.reduce((s: number, p: any) => s + Number(p.valorTotal || 0), 0) || 0;
        const servs = row.InvoiceService?.reduce((s: number, p: any) => s + Number(p.valorTotal || 0), 0) || 0;
        return Number(row.valorTotalNota || (prods + servs));
      },
      cell: ({ getValue }) => {
        const v = getValue() as number;
        return <strong>{v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—'}</strong>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Emissão',
      cell: ({ getValue }) => {
        const v = getValue() as string;
        return v ? new Date(v).toLocaleDateString('pt-BR') : '—';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const label = mapStatus(getValue() as string);
        return (
          <Badge type={label}>
            <Dot type={label} />
            {label}
          </Badge>
        );
      },
    },
    {
      id: 'acoes',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const { id, status, serie, numeroNF } = row.original;
        const danfe = row.original.linkDanfe as string | undefined;
        const label = serie && numeroNF ? `${serie}.${String(numeroNF).padStart(6, '0')}` : `#${id}`;

        // Ação primária contextual: rascunho/negada → emitir (abre edição/emissão);
        // emitindo → desabilitado; emitida → cancelar (com confirmação); cancelada → off.
        const renderPrimary = () => {
          if (status === 'RASCUNHO' || status === 'NEGADA') {
            return (
              <span title="Emitir" style={{ cursor: 'pointer', display: 'flex' }} onClick={() => openModal(id)}>
                <Send size={18} color="#0085CC" />
              </span>
            );
          }
          if (status === 'EMITINDO') {
            return (
              <span title="Emitindo…" style={{ cursor: 'not-allowed', display: 'flex' }}>
                <Spinner size={18} color="#CCC" />
              </span>
            );
          }
          if (status === 'EMITIDA') {
            return (
              <span title="Cancelar NF-e" style={{ cursor: 'pointer', display: 'flex' }} onClick={() => setCancelTarget({ id, label })}>
                <Ban size={18} color="#F75A68" />
              </span>
            );
          }
          // CANCELADA ou status desconhecido → sem ação
          return (
            <span title="Sem ação disponível" style={{ cursor: 'not-allowed', display: 'flex' }}>
              <Ban size={18} color="#CCC" />
            </span>
          );
        };

        return (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {renderPrimary()}
            <span title="Visualizar" style={{ cursor: 'pointer', display: 'flex' }} onClick={() => openView(id)}>
              <Eye size={18} color="#666" />
            </span>
            {danfe ? (
              <span title="Abrir DANFE" style={{ cursor: 'pointer', display: 'flex' }} onClick={() => window.open(danfe, '_blank', 'noopener')}>
                <FileText size={18} color="#666" />
              </span>
            ) : (
              <span title="DANFE indisponível" style={{ cursor: 'not-allowed', display: 'flex' }}>
                <FileText size={18} color="#CCC" />
              </span>
            )}
            <span title="Anexos (em breve)" style={{ cursor: 'not-allowed', display: 'flex' }}>
              <Paperclip size={18} color="#CCC" />
            </span>
          </div>
        );
      },
    },
  ], [openModal, openView]);

  const table = useReactTable({
    data: invoices,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
    <GlobalTable>
      <thead>
        <HeaderLine>
          {table.getHeaderGroups().map(hg =>
            hg.headers.map(header => (
              <Th
                key={header.id}
                $sortable={header.column.getCanSort()}
                onClick={header.column.getToggleSortingHandler()}
              >
                <ThContent>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {(() => {
                    const dir = header.column.getIsSorted() as string;
                    if (dir === 'asc') return <ChevronUp size={12} />;
                    if (dir === 'desc') return <ChevronDown size={12} />;
                    if (header.column.getCanSort()) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
                    return null;
                  })()}
                </ThContent>
              </Th>
            ))
          )}
          <th
            ref={columnMenuRef}
            style={{ width: '32px', background: '#f5f5f5', border: 0, padding: '0 4px', verticalAlign: 'middle' }}
          >
            <ConfigMenuWrapper>
              <ConfigMenuButton onClick={() => setShowColumnMenu(v => !v)} title="Mostrar/ocultar colunas">
                <Settings size={16} />
              </ConfigMenuButton>
              {showColumnMenu && (
                <ColumnMenu>
                  <ColumnMenuList>
                    {table.getAllLeafColumns().map(col => {
                      if (col.id === 'acoes') return null;
                      const visible = col.getIsVisible();
                      return (
                        <ColumnMenuItem key={col.id}>
                          <HiddenCheckbox checked={visible} onChange={col.getToggleVisibilityHandler()} />
                          <StyledCheckbox $checked={visible} />
                          {col.columnDef.header as string}
                        </ColumnMenuItem>
                      );
                    })}
                  </ColumnMenuList>
                </ColumnMenu>
              )}
            </ConfigMenuWrapper>
          </th>
        </HeaderLine>
      </thead>
      <tbody>
        {loading ? (
          <Tr>
            <Td colSpan={columns.length + 1}>
              <SpinnerWrapper>
                <Spinner size={32} color="#0085CC" />
                <span style={{ marginLeft: '12px', color: '#666' }}>Carregando notas fiscais...</span>
              </SpinnerWrapper>
            </Td>
          </Tr>
        ) : error ? (
          <Tr>
            <Td colSpan={columns.length + 1} style={{ textAlign: 'center', color: '#9B111E', padding: '20px' }}>
              {error}
            </Td>
          </Tr>
        ) : table.getRowModel().rows.length === 0 ? (
          <Tr>
            <Td colSpan={columns.length + 1} style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              Nenhuma nota fiscal encontrada.
            </Td>
          </Tr>
        ) : (
          table.getRowModel().rows.map(row => (
            <Tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
              <Td />
            </Tr>
          ))
        )}
      </tbody>
    </GlobalTable>

    <ConfirmationModal
      isOpen={!!cancelTarget}
      title="Cancelar NF-e"
      message={
        cancelTarget
          ? `Tem certeza que deseja cancelar a nota ${cancelTarget.label}? O cancelamento é irreversível e só é permitido em até 24h da autorização.`
          : ''
      }
      actions={[
        { label: 'Voltar', onClick: () => setCancelTarget(null), variant: 'outline' },
        {
          label: cancelLoading ? 'Cancelando…' : 'Cancelar NF-e',
          onClick: handleConfirmCancel,
          variant: 'danger',
        },
      ]}
    />
    </>
  );
};

export default InvoiceTable;
